import { FC } from "react";
import { PositionAsset } from "./types";
import { LoanAsset } from "./components/loan-asset";
import { usePositionsPanelContext } from "./position-panel-context";
import { LiquidationPriceState, getLiquidationPriceState } from "./utils";
import { match } from "ts-pattern";
import {
  ActionButton,
  ActionButtonGroup,
} from "./components/action-button-group";
import { AprDisplay } from "../apr-display";
import { AssetAddressContext } from "../asset-address-context";
import { DisabledCollateralWarning } from "../disabled-collateral-warning";
import { useDescriptionVariant } from "../description/use-description-variant";
import { DescriptionExternalLink } from "../description";

interface DepositAssetProps {
  asset: PositionAsset;
  rewardsSymbol: string;
  divider?: boolean;
}

export const DepositAsset: FC<DepositAssetProps> = ({
  asset,
  rewardsSymbol,
  divider = false,
}) => {
  const {
    assetAddress,
    symbol,
    collateralBalance,
    collateralOnlyBalance,
    decimals,
    depositApr,
    depositRewardsApr,
    merklRewards,
    points,
  } = asset;
  const { useQuotePrice, positions, links, disabledCollateralDescription } =
    usePositionsPanelContext();
  const quotePrice = useQuotePrice();

  const liquidationPriceState = getLiquidationPriceState({
    positions,
    asset,
    quotePrice,
  });

  const withdrawLink = links.getWithdrawLink(
    positions.siloAddress,
    asset.assetAddress
  );
  const depositLink = links.getDepositLink(
    positions.siloAddress,
    asset.assetAddress
  );

  const collateralPoints = points.filter(({ type }) => type === "collateral");
  const protectedPoints = points.filter(({ type }) => type === "protected");

  return (
    <div
      className="flex flex-col gap-5 @screen-sm:gap-8"
      data-cy="deposit-asset-position"
    >
      {match(asset)
        .when(
          ({ collateralBalance }) => collateralBalance > 0n,
          () => (
            <LoanAsset
              assetAddress={assetAddress}
              symbol={symbol}
              decimals={decimals}
              balance={collateralBalance}
              balanceType="Borrowable deposit"
              balanceLabel="Deposited"
              aprSlot={
                <AprDisplay.PositionDeposit
                  depositApr={depositApr}
                  depositRewardsApr={depositRewardsApr}
                  isProtected={false}
                  rewardsSymbol={rewardsSymbol}
                  merklRewards={merklRewards}
                />
              }
              liquidationPriceState={liquidationPriceState}
              liquidationPriceDescription={
                <LiquidationPriceDescription
                  liquidationPriceState={liquidationPriceState}
                />
              }
              divider={divider}
              points={collateralPoints}
            />
          )
        )
        .when(
          ({ collateralOnlyBalance }) => collateralOnlyBalance > 0n,
          () => (
            <LoanAsset
              assetAddress={assetAddress}
              symbol={symbol}
              decimals={decimals}
              balance={collateralOnlyBalance}
              balanceType={
                <span className="text-amber-300">Non-borrowable deposit</span>
              }
              balanceLabel="Deposited"
              aprSlot={
                <AprDisplay.PositionDeposit
                  depositApr={depositApr}
                  depositRewardsApr={depositRewardsApr}
                  isProtected={true}
                  rewardsSymbol={rewardsSymbol}
                  merklRewards={merklRewards}
                />
              }
              liquidationPriceState={liquidationPriceState}
              liquidationPriceDescription={
                <LiquidationPriceDescription
                  liquidationPriceState={liquidationPriceState}
                />
              }
              divider={divider}
              points={protectedPoints}
            />
          )
        )
        .otherwise(() => null)}

      {divider && <div className="h-px bg-shale-700" />}

      {asset.isDisabledCollateral && (
        <AssetAddressContext.Provider value={asset.assetAddress}>
          <DisabledCollateralWarning
            assetSymbol={asset.symbol}
            variant="panel"
            description={disabledCollateralDescription}
          />
        </AssetAddressContext.Provider>
      )}

      <ActionButtonGroup>
        <ActionButton
          href={withdrawLink}
          data-cy="deposit-card-withdraw-button"
        >
          Withdraw
        </ActionButton>

        <ActionButton href={depositLink} data-cy="deposit-card-deposit-button">
          Deposit more
        </ActionButton>
      </ActionButtonGroup>
    </div>
  );
};

interface LiquidationPriceDescriptionProps {
  liquidationPriceState: LiquidationPriceState;
}

const LiquidationPriceDescription: FC<LiquidationPriceDescriptionProps> = ({
  liquidationPriceState,
}) => {
  const { links } = usePositionsPanelContext();
  const descriptionVariant = useDescriptionVariant();

  const notApplicableClassName = match(descriptionVariant)
    .with("static", () => "text-white")
    .with("tooltip", () => "text-amber-300")
    .exhaustive();

  return (
    <div className="flex flex-col gap-5">
      <p>
        This is the simulated asset price at which the borrow position becomes
        insolvent (HF goes to 0%).
      </p>

      <p>
        If you have deposited two assets, the liquidation price for each asset
        is weighted to reflect its impact on the position.
      </p>

      {liquidationPriceState._tag === "notApplicable" && (
        <p>
          <span className={`font-medium ${notApplicableClassName}`}>
            "--" (no value)
          </span>{" "}
          means the asset's weight of the total deposit position is
          insignificant such that even if the asset's value drops to $0, it will
          not cause liquidation.
        </p>
      )}

      <div>
        <DescriptionExternalLink
          className="inline-block lg:text-base"
          variant="action"
          href={links.liquidationPrice}
        />
      </div>
    </div>
  );
};
